import { observer } from 'mobx-react'
import React, { useContext } from 'react'
import styles from './DocumentPermissionsTab.sass'
import map from 'lodash/map'
import { ModalSheetListHeader, ModalSheetSection } from 'components/ModalSheet'
import { CurrentMemberContext } from 'contexts/CurrentMemberContext'
import DocumentAccess from 'documents/DocumentAccess'
import { DocumentDetailsModalViewModel } from '../DocumentDetailsModalViewModel'
import Avatar from 'components/Avatar'
import Button from 'components/Button'
import {
  Dropdown,
  DropdownTitle,
  DropdownItem,
  DropdownItemTitle,
  DropdownItemDescription,
} from 'components/Dropdown'
import Icon from 'components/Icon'
import { IconButton } from 'components/IconButton'
import Tooltip from 'components/Tooltip'
import { DocumentJobAccessEditPermission } from 'documents/dtos/document-access'
import { useBrandingSettings } from 'hooks/branding'

interface DocumentPermissionsProps {
  vm: DocumentDetailsModalViewModel
}

export default observer(function DocumentPermissionsTab({
  vm,
}: DocumentPermissionsProps) {
  const jobAccesses = vm.document.accesses.filter((x) => x.type === 'Job')
  return (
    <div className={styles.documentPermissions}>
      <ModalSheetSection bordered>
        <ModalSheetListHeader>
          Linked Jobs
          {/* <div className={styles.sectionHeader}>Linked Jobs</div> */}
        </ModalSheetListHeader>
      </ModalSheetSection>
      <div className={styles.accessSection}>
        <div className={styles.accesses}>
          {jobAccesses.length === 0 ? (
            <div className={styles.noAccesses}>
              <p className={styles.noAccessesTitle}>Job Linking</p>
              <p className={styles.noAccessesDescription}>
                This document hasn't been shared in a Job.
                <br />
                You can link documents to jobs so everyone working on the job
                can access them, as well as make changes (if you allow it).
                <br /> <br />
                You can see which jobs this document has been linked to here.
              </p>
            </div>
          ) : (
            jobAccesses.map((access, index) => (
              <div key={index}>
                <JobAccessItem
                  title={access.job ? access.job.name : '<Unknown Job>'}
                  access={access}
                  vm={vm}
                />
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
})

const jobPermissionDescription = {
  Owner: {
    title: 'Just me',
    description: 'Only you will be able to make changes.',
    disabled: false,
    value: 'Owner',
  },
  Clients: {
    title: 'Clients',
    description: 'Only clients on this job can make changes.',
    disabled: true,
    value: 'Clients',
  },
  Everyone: {
    title: 'Everyone',
    description: 'Any person on this job can make changes.',
    disabled: false,
    value: 'Everyone',
  },
  Unknown: {
    title: 'Unspecified',
    description: '',
    disabled: true,
    value: null,
  },
}

interface JobAccessProps extends React.HTMLAttributes<HTMLButtonElement> {
  access: DocumentAccess
  title: string
  vm: DocumentDetailsModalViewModel
}

const JobAccessItem = observer(({ access, vm, ...props }: JobAccessProps) => {
  const current =
    access.editPermission === null
      ? jobPermissionDescription.Unknown
      : jobPermissionDescription[access.editPermission]
  const currentMember = useContext(CurrentMemberContext)
  const { themePrimary } = useBrandingSettings()

  return (
    <AccessItem access={access} vm={vm} {...props}>
      <Dropdown
        disabled={currentMember !== vm.document.owner}
        position="bottom-end"
        autoDismiss
        button={
          <Button size="small" iconRight={<Icon rotate={90} name="arrow" />}>
            {current.title}
          </Button>
        }
      >
        <DropdownTitle>Who can edit this document?</DropdownTitle>
        {map(
          jobPermissionDescription,
          (opts, key) =>
            !opts.disabled && (
              <DropdownItem
                key={key}
                onClick={() =>
                  vm.changeAccessPermission(
                    access,
                    opts.value as DocumentJobAccessEditPermission,
                  )
                }
                icon={
                  <Icon
                    color={themePrimary}
                    name={
                      key === access.editPermission
                        ? 'radio-checked'
                        : 'radio-unchecked'
                    }
                  />
                }
              >
                <div className={styles.permission}>
                  <DropdownItemTitle className={styles.permissionTitle}>
                    {opts.title}
                  </DropdownItemTitle>
                  <DropdownItemDescription
                    className={styles.permissionSubtitle}
                  >
                    {opts.description}
                  </DropdownItemDescription>
                </div>
              </DropdownItem>
            ),
        ).filter((x) => x)}
      </Dropdown>
    </AccessItem>
  )
})

const AccessItem = observer(
  ({ title, access, children, vm }: JobAccessProps) => {
    return (
      <div className={styles.accessItem}>
        <div className={styles.accessAvatar}>
          <Avatar subject={access.grantor} size={45} />
        </div>
        <div className={styles.accessInfo}>
          <p className={styles.accessTitle}>{title}</p>
          <p
            className={styles.accessSubtitle}
            title={`Access created by ${access.grantor.displayName}`}
          >
            Linked by{' '}
            <span className={styles.accessGrantor}>
              {access.grantor.isCurrentUser
                ? 'you'
                : access.grantor.displayName}
            </span>
          </p>
        </div>
        <div className={styles.accessContent}>{children}</div>
        <div className={styles.accessActions}>
          {vm.document.canManageAccess && (
            <Tooltip content="Unlink from this job">
              <IconButton onClick={() => vm.deleteAccess(access)}>
                <Icon name="close" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </div>
    )
  },
)
